import { useMemo } from 'react'

import FuzzySearch from 'fuzzy-search'

import { Member, Space, Post } from '@tribeplatform/gql-client/types'

import { useMemberActionItems } from '../../Member/hooks/useMemberActionItems.js'
import { usePostActionItems } from '../../Post/hooks/usePostActionItems.js'
import { useSpaceActionItems } from '../../Space/hooks/useSpaceActionItems.js'
import { useCommandBar } from '../CommandBarContext.js'
import { CommandBarActionType } from '../types.js'

import {
  useCommandBarAdminSettings,
  useCommandBarNavigation,
  useCommandBarPosts,
} from './index.js'

export const useQueryCommands = (query: string) => {
  const {
    context: baseContext,
    options: { context: customContext, excludeActions = [] },
  } = useCommandBar()
  const context = customContext ?? baseContext

  const { actions: postsActions } = useCommandBarPosts()
  const { actions: navigationActions } = useCommandBarNavigation()
  const { actions: adminSettingsActions } = useCommandBarAdminSettings()

  const contextPost = context.find(c => c?.entityType === 'post')
    ?.entity as Post
  const contextSpace = context.find(c => c?.entityType === 'space')
    ?.entity as Space
  const contextMember = context.find(c => c?.entityType === 'member')
    ?.entity as Member
  const { actions: postActions, shortcuts: postShortcuts } = usePostActionItems(
    {
      post: contextPost ?? null,
      context: 'post',
    },
  )

  const { actions: spaceActions } = useSpaceActionItems({
    space: contextSpace ?? null,
  })

  const { actions: memberActions } = useMemberActionItems({
    member: contextMember ?? null,
    context: 'commandbar',
  })

  const entityActions = useMemo(
    () => [
      ...postActions,
      ...(postShortcuts ?? []),
      ...memberActions,
      ...spaceActions,
    ],
    [postActions, postShortcuts, memberActions, spaceActions],
  )

  const actions = useMemo(
    () => [
      ...(excludeActions.includes('entity') ? [] : entityActions),
      ...(excludeActions.includes('posts') ? [] : postsActions),
      ...(excludeActions.includes('navigations') ? [] : navigationActions),
      ...(excludeActions.includes('adminSettings') ? [] : adminSettingsActions),
    ],
    [
      excludeActions,
      entityActions,
      postsActions,
      navigationActions,
      adminSettingsActions,
    ],
  )

  const searcher = useMemo(
    () =>
      new FuzzySearch(
        actions.filter(action => !!action),
        ['name'],
        {
          caseSensitive: false,
        },
      ),
    [actions],
  )

  const filteredActions = useMemo(() => {
    return query ? (searcher.search(query) as CommandBarActionType[]) : actions
  }, [query, searcher, actions])

  const actionsByCategory = useMemo(
    () =>
      filteredActions.reduce((acc, action) => {
        if (!acc[action?.category]) {
          acc[action?.category] = []
        }
        acc[action?.category].push(action)
        return acc
      }, {}),
    [filteredActions],
  )

  return {
    actions,
    filteredActions,
    actionsByCategory,
  }
}
